import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  assignmentCohortView,
  describeTarget,
  type Assignment,
  type AssignmentTarget,
  type ClassMember,
} from '@yuan-math/learning';
import { Badge, Button, Card, MasteryBar, SectionHeading } from '@yuan-math/ui';
import { registry } from '../state/LearnerContext.tsx';
import { createAssignment, deleteAssignment, fetchAssignments } from '../state/classroom.ts';

/**
 * Setting and monitoring assignments.
 *
 * The form defaults to a mastery target on purpose. A teacher who wants "twenty
 * questions" can still have it, but the path of least resistance should be the
 * one that measures learning rather than time served.
 *
 * Nothing here tracks submission: progress is derived from each student's own
 * profile, so an assignment completes itself as they learn.
 */
export function Assignments({
  classId,
  members,
  focusConceptIds,
}: {
  classId: string;
  members: ClassMember[];
  focusConceptIds: string[];
}): JSX.Element {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [composing, setComposing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchAssignments(classId).then((rows) => {
      if (!cancelled) setAssignments(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [classId]);

  return (
    <section className="dashboard-section">
      <SectionHeading
        title="Assignments"
        description="Set a standard to reach rather than a number of questions. Progress updates itself as students work — there is nothing to hand in."
        action={
          <Button variant={composing ? 'ghost' : 'secondary'} size="sm" onClick={() => setComposing((open) => !open)}>
            {composing ? 'Cancel' : 'New assignment'}
          </Button>
        }
      />

      {composing ? (
        <AssignmentForm
          classId={classId}
          suggestedConceptIds={focusConceptIds}
          onCreated={(created) => {
            setAssignments((current) => [created, ...current]);
            setComposing(false);
          }}
        />
      ) : null}

      {assignments.length === 0 && !composing ? (
        <Card>
          <p>
            Nothing set yet. An assignment names concepts and a standard — reach mastery, practise a number of
            questions, or simply read the lesson.
          </p>
        </Card>
      ) : null}

      <div className="assignment-list">
        {assignments.map((assignment) => (
          <AssignmentCard
            key={assignment.id}
            assignment={assignment}
            members={members}
            onDeleted={() => setAssignments((current) => current.filter((entry) => entry.id !== assignment.id))}
          />
        ))}
      </div>
    </section>
  );
}

function AssignmentCard({
  assignment,
  members,
  onDeleted,
}: {
  assignment: Assignment;
  members: ClassMember[];
  onDeleted(): void;
}): JSX.Element {
  const now = Date.now();
  const view = useMemo(() => assignmentCohortView(assignment, members, now), [assignment, members, now]);
  const [expanded, setExpanded] = useState(false);

  const due = assignment.dueAt ? new Date(assignment.dueAt) : undefined;
  const late = view.overdueCount > 0;

  return (
    <Card className="assignment">
      <div className="assignment__head">
        <div>
          <h3 className="assignment__title">{assignment.title}</h3>
          <p className="assignment__meta">
            {describeTarget(assignment.target)} ·{' '}
            {assignment.conceptIds.map((id) => registry.concept(id)?.title ?? id).join(', ')}
            {due ? ` · due ${due.toLocaleDateString()}` : ''}
          </p>
          {assignment.note ? <p className="assignment__note">{assignment.note}</p> : null}
        </div>
        <div className="assignment__badges">
          {late ? <Badge tone="warn">{view.overdueCount} overdue</Badge> : null}
          <Badge tone={view.completedCount === members.length && members.length > 0 ? 'correct' : 'neutral'}>
            {view.completedCount} of {members.length} done
          </Badge>
        </div>
      </div>

      <MasteryBar value={view.averageFraction} label="Class progress" />

      {view.hardestConceptIds.length > 0 && view.completedCount < members.length ? (
        <p className="assignment__hardest">
          Most students are still short on{' '}
          <strong>{view.hardestConceptIds.map((id) => registry.concept(id)?.title ?? id).join(', ')}</strong>.
        </p>
      ) : null}

      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setExpanded((open) => !open)}>
        {expanded ? 'Hide students' : 'See who is where'}
      </button>

      {expanded ? (
        <div className="table-wrap">
          <table className="progress-table">
            <thead>
              <tr>
                <th scope="col">Student</th>
                <th scope="col">Progress</th>
                <th scope="col">Still to do</th>
              </tr>
            </thead>
            <tbody>
              {view.rows.map((row) => (
                <tr key={row.profileId}>
                  <th scope="row">{row.displayName}</th>
                  <td><MasteryBar value={row.progress.fraction} size="sm" /></td>
                  <td className="assignment__remaining">
                    {row.progress.complete
                      ? 'Done'
                      : row.progress.concepts
                          .filter((concept) => !concept.met)
                          .map((concept) => `${registry.concept(concept.conceptId)?.title ?? concept.conceptId}: ${concept.remaining}`)
                          .join(' · ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="assignment__actions">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => {
            if (!window.confirm(`Delete "${assignment.title}"? Students keep everything they have learned.`)) return;
            void deleteAssignment(assignment.id).then((ok) => {
              if (ok) onDeleted();
            });
          }}
        >
          Delete
        </button>
      </div>
    </Card>
  );
}

function AssignmentForm({
  classId,
  suggestedConceptIds,
  onCreated,
}: {
  classId: string;
  suggestedConceptIds: string[];
  onCreated(assignment: Assignment): void;
}): JSX.Element {
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState<string[]>(suggestedConceptIds.slice(0, 2));
  const [kind, setKind] = useState<AssignmentTarget['kind']>('mastery');
  const [attempts, setAttempts] = useState(10);
  const [threshold, setThreshold] = useState(85);
  const [dueDate, setDueDate] = useState('');
  const [busy, setBusy] = useState(false);

  const available = registry.availableConcepts();

  const target = (): AssignmentTarget => {
    if (kind === 'effort') return { kind: 'effort', attemptsPerConcept: attempts };
    if (kind === 'lesson') return { kind: 'lesson' };
    return { kind: 'mastery', threshold: threshold / 100 };
  };

  return (
    <Card className="assignment-form">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          void createAssignment({
            classId,
            title: title.trim() || 'Untitled assignment',
            ...(note.trim() ? { note: note.trim() } : {}),
            conceptIds: selected,
            target: target(),
            ...(dueDate ? { dueAt: new Date(`${dueDate}T23:59:59`).getTime() } : {}),
          })
            .then((created) => {
              if (created) onCreated(created);
            })
            .finally(() => setBusy(false));
        }}
      >
        <label className="field">
          <span className="field__label">Title</span>
          <input
            className="field__input"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="e.g. Fractions before Friday"
            maxLength={80}
          />
        </label>

        <fieldset className="field">
          <legend className="field__label">Concepts</legend>
          <div className="concept-picker">
            {available.map((concept) => {
              const chosen = selected.includes(concept.id);
              return (
                <button
                  key={concept.id}
                  type="button"
                  className={`chip ${chosen ? 'chip--due' : ''}`}
                  aria-pressed={chosen}
                  onClick={() =>
                    setSelected((current) =>
                      chosen ? current.filter((id) => id !== concept.id) : [...current, concept.id],
                    )
                  }
                >
                  {concept.title}
                </button>
              );
            })}
          </div>
          <span className="field__hint">{selected.length} selected</span>
        </fieldset>

        <fieldset className="field">
          <legend className="field__label">What counts as done?</legend>
          <div className="target-options">
            <label className="target-option">
              <input type="radio" checked={kind === 'mastery'} onChange={() => setKind('mastery')} />
              <span>
                <strong>Reach mastery</strong>
                <span className="target-option__note">
                  Measures understanding. A student who already knows it is finished straight away.
                </span>
              </span>
            </label>
            {kind === 'mastery' ? (
              <label className="field target-detail">
                <span className="field__label">Mastery needed: {threshold}%</span>
                <input
                  type="range"
                  min={50}
                  max={95}
                  step={5}
                  value={threshold}
                  onChange={(event) => setThreshold(Number(event.target.value))}
                />
              </label>
            ) : null}

            <label className="target-option">
              <input type="radio" checked={kind === 'effort'} onChange={() => setKind('effort')} />
              <span>
                <strong>Practise a set number of questions</strong>
                <span className="target-option__note">For a warm-up or revision, where the practice is the point.</span>
              </span>
            </label>
            {kind === 'effort' ? (
              <label className="field target-detail">
                <span className="field__label">Questions per concept</span>
                <input
                  className="field__input"
                  type="number"
                  min={1}
                  max={50}
                  value={attempts}
                  onChange={(event) => setAttempts(Number(event.target.value))}
                />
              </label>
            ) : null}

            <label className="target-option">
              <input type="radio" checked={kind === 'lesson'} onChange={() => setKind('lesson')} />
              <span>
                <strong>Read the lesson</strong>
                <span className="target-option__note">For an introduction, before practice makes sense.</span>
              </span>
            </label>
          </div>
        </fieldset>

        <label className="field">
          <span className="field__label">Due date (optional)</span>
          <input
            className="field__input"
            type="date"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
          />
          <span className="field__hint">Without one, the work simply stays open.</span>
        </label>

        <label className="field">
          <span className="field__label">Note for students (optional)</span>
          <input
            className="field__input"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={200}
            placeholder="e.g. Focus on common denominators — that is what tripped us up."
          />
        </label>

        <Button type="submit" disabled={busy || selected.length === 0}>
          {busy ? 'Setting…' : 'Set assignment'}
        </Button>
      </form>
    </Card>
  );
}

/**
 * The student's view of what has been set.
 *
 * Shown on the dashboard above the platform's own recommendations, because a
 * teacher's instruction outranks an algorithm's suggestion.
 */
export function StudentAssignments({
  entries,
}: {
  entries: { assignment: Assignment; progress: import('@yuan-math/learning').AssignmentProgress }[];
}): JSX.Element | null {
  if (entries.length === 0) return null;

  return (
    <section className="dashboard-section">
      <SectionHeading title="Set by your teacher" description="These come first." />
      <ul className="recommendation-list">
        {entries.map(({ assignment, progress }) => (
          <li key={assignment.id}>
            <Card className={`recommendation ${progress.complete ? 'recommendation--done' : ''}`}>
              <div className="recommendation__body">
                <span className={`recommendation__kind recommendation__kind--${progress.overdue ? 'repair' : progress.complete ? 'advance' : 'review'}`}>
                  {progress.complete ? 'Done' : progress.overdue ? 'Overdue' : describeTarget(assignment.target)}
                </span>
                <h3 className="recommendation__title">{assignment.title}</h3>
                {assignment.note ? <p className="recommendation__reason">{assignment.note}</p> : null}
                <p className="recommendation__reason">
                  {progress.complete
                    ? 'Nothing left to do on this one.'
                    : progress.concepts
                        .filter((concept) => !concept.met)
                        .map((concept) => registry.concept(concept.conceptId)?.title ?? concept.conceptId)
                        .join(', ')}
                </p>
                {!progress.complete ? <MasteryBar value={progress.fraction} size="sm" /> : null}
              </div>
              {!progress.complete ? (
                <div className="recommendation__actions">
                  {(() => {
                    const next = progress.concepts.find((concept) => !concept.met);
                    if (!next) return null;
                    const lesson = registry.lessonsForConcept(next.conceptId)[0];
                    return (
                      <>
                        {lesson ? (
                          <Link to={`/lessons/${lesson.id}`} className="btn btn--secondary btn--sm">
                            Lesson
                          </Link>
                        ) : null}
                        <Link to={`/practice/${next.conceptId}`} className="btn btn--primary btn--sm">
                          Practise
                        </Link>
                      </>
                    );
                  })()}
                </div>
              ) : null}
            </Card>
          </li>
        ))}
      </ul>
    </section>
  );
}
